import React, { FC, Fragment } from 'react';
import {
  Slider as SliderContainer,
  Rail,
  Handles,
  Tracks,
  Ticks,
} from 'react-compound-slider';
import {
  Rail as StyledRail,
  RailHotspot,
  Track,
  TrackHotspot,
  Dot,
  TickIndicator,
  TickValue,
} from './slider.style';
import { SliderProps, SliderTypes } from './slider.types';
import { withTheme } from 'styled-components';

const Slider: FC<SliderProps> = ({
  minValue = 0,
  maxValue,
  defaultValues,
  step = 1,
  mode = 2,
  vertical = false,
  reversed = false,
  handlers = {},
  disabled = false,
  theme = {},
  trackLeft = false,
  trackRight = false,
  enableRailClick = false,
  enableTrackClick = false,
  tickValues,
  showTickValues = false,
  renderTicks,
  className,
  renderRail,
  renderHandles,
  renderTracks,
  ...otherProps
}) => {

  const values = defaultValues || [minValue];
  const domain = [minValue, maxValue];
  const themeType: string = theme.dark ? SliderTypes.DARK : SliderTypes.PRIMARY;
  const rootSyle: object = {
    position: 'relative',
    height: vertical ? '100%' : 'auto',
  };

  return (
    <SliderContainer
      domain={domain}
      values={values}
      step={step}
      mode={mode}
      vertical={vertical}
      reversed={reversed}
      disabled={disabled}
      className={className}
      rootProps={otherProps}
      rootStyle={rootSyle}
      {...handlers}
    >
      <Rail>
        {({ getEventData, activeHandleID, getRailProps }) => {

          if (renderRail) {

            return renderRail({ getEventData, activeHandleID, getRailProps });

          }

          return (
            <>
              <StyledRail
                theme={themeType}
                vertical={vertical}
              />
              {enableRailClick && (
                <RailHotspot
                  {...getRailProps()}
                  vertical={vertical}
                />
              )}
            </>
          );

        }}
      </Rail>
      <Handles>
        {({ handles, getHandleProps, activeHandleID }) => {

          if (renderHandles) {

            return renderHandles({ handles, getHandleProps, activeHandleID });

          }

          return (
            <>
              {handles.map(handle => (
                <Dot
                  key={handle.id}
                  {...getHandleProps(handle.id)}
                  left={`${handle.percent}%`}
                  theme={themeType}
                  role={'slider'}
                  aria-valuemin={domain[0]}
                  aria-valuemax={domain[1]}
                  aria-valuenow={handle.value}
                  vertical={vertical}
                  isActive={activeHandleID === handle.id}
                />
              ))}
            </>
          );

        }}
      </Handles>
      <Tracks right={trackRight} left={trackLeft}>
        {({ getEventData, activeHandleID, tracks, getTrackProps }) => {

          if (renderTracks) {

            return renderTracks({ getEventData, activeHandleID, tracks, getTrackProps });

          }

          return (
            <>
              {tracks.map(({ id, source, target }) => {
                const left = `${source.percent}%`;
                const width = `${target.percent - source.percent}%`;
                return (
                  <Fragment key={id}>
                    <Track
                      left={left}
                      width={width}
                      theme={themeType}
                      vertical={vertical}
                    />
                    {enableTrackClick &&
                      (<TrackHotspot
                        left={left}
                        width={width}
                        vertical={vertical}
                        {...getTrackProps()}
                      />)
                    }
                  </Fragment>
                );
              })}
            </>
          );

        }}
      </Tracks>
      {
        tickValues && (
          <Ticks values={tickValues}>
            {({ getEventData, activeHandleID, ticks }) => {

              if (renderTicks) {

                return renderTicks({ getEventData, activeHandleID, ticks });

              }

              return (
                <>
                  {ticks.map(tick => (
                    <Fragment key={tick.id}>
                      <TickIndicator
                        tickPercent={tick.percent}
                        vertical={vertical}
                        theme={themeType}
                      />
                      {showTickValues && (
                        <TickValue
                          count={ticks.length}
                          tickPercent={tick.percent}
                          vertical={vertical}
                          theme={themeType}
                        >{tick.value}</TickValue>)
                      }
                    </Fragment>
                  ))}
                </>
              );

            }}
          </Ticks>
        )
      }
    </SliderContainer>
  );
};

export default withTheme(Slider);
